import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import API from "@/lib/api";
import { NULLSPACE_FALLBACK } from "@/data/leaderboard";

const ORG_COLORS = {
  Anthropic: "#e8a87c",
  Google:    "#4285f4",
  OpenAI:    "#74aa9c",
};

function OrgDot({ org }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: ORG_COLORS[org] || "#888", flexShrink: 0, marginRight: 6,
    }} />
  );
}

export default function NullspaceDetailPage() {
  const [results, setResults] = useState(NULLSPACE_FALLBACK);

  useEffect(() => {
    axios.get(`${API}/hf/nullspace-leaderboard`).then(r => {
      if (r.data?.rows?.length) setResults(r.data.rows);
    }).catch(() => {});
  }, []);

  const maxAccuracy = Math.max(...results.map(r => r.accuracy));

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>NULLSPACE — JEE Questions Frontier Models Get Wrong · Nalandadata.ai</title>
        <meta name="description" content="NULLSPACE: 267 JEE questions every frontier model fails. 43 of 267 failed by all three simultaneously. Claude Opus 5 scores 67.3% on uncontaminated rows — a third still fail." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/benchmarks/nullspace" />
        <meta property="og:title" content="NULLSPACE — JEE Questions Frontier Models Get Wrong · Nalandadata.ai" />
        <meta property="og:description" content="NULLSPACE: 267 JEE questions every frontier model fails. 43 of 267 failed by all three simultaneously. Claude Opus 5 scores 67.3% on uncontaminated rows." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NULLSPACE — JEE Questions Frontier Models Get Wrong · Nalandadata.ai" />
        <meta name="twitter:description" content="NULLSPACE: 267 JEE questions every frontier model fails. 43 of 267 failed by all three simultaneously. Claude Opus 5 scores 67.3% on uncontaminated rows." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>

      {/* Breadcrumb */}
      <div className="s-breadbar">
        <div className="s-wrap s-breadbar-inner">
          <Link to="/">Home</Link>
          <span style={{ opacity: .4 }}>/</span>
          <Link to="/benchmarks">Benchmarks</Link>
          <span style={{ opacity: .4 }}>/</span>
          <span>NULLSPACE</span>
        </div>
      </div>

      {/* Page header */}
      <div className="s-detail-header">
        <div className="s-wrap">
          <p className="s-eyebrow">STEM Reasoning · JEE · Failure-Only Benchmark</p>
          <h1>The questions frontier models <span className="hl">actually get wrong.</span></h1>
          <p className="sub">Frontier models score 96–98% on ordinary JEE questions. NULLSPACE contains only the questions they fail — every one independently verified with a correct answer key. <b>43 of 267 were failed by all three models simultaneously.</b> On the uncontaminated rows Claude still fails a third of them.</p>
        </div>
      </div>

      {/* Stat strip */}
      <div className="s-stat-strip">
        <div className="s-stat-item">
          <div className="s-stat-num">267</div>
          <div className="s-stat-lbl">Public questions — every one failed by at least one frontier model, answer key independently confirmed.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">43 <span className="s-stat-unit">of 267</span></div>
          <div className="s-stat-lbl">Failed by all three models simultaneously — 16% of the set. Only 23 were solved by all three.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">67.3<span className="s-stat-unit">%</span></div>
          <div className="s-stat-lbl">Claude Opus 5 accuracy on 165 clean rows — questions it had no hand in selecting or verifying.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">77</div>
          <div className="s-stat-lbl">Questions held out and unpublished so future models can still be honestly measured.</div>
        </div>
      </div>

      {/* Key finding callout */}
      <section className="s-band" style={{ padding: "48px 0" }}>
        <div className="s-wrap">
          <div style={{
            background: "linear-gradient(135deg, rgba(255,200,100,0.06) 0%, rgba(255,150,50,0.04) 100%)",
            border: "1px solid rgba(255,200,100,0.18)",
            borderRadius: 12, padding: "32px 36px",
          }}>
            <p className="s-eyebrow" style={{ color: "#f5a623", marginBottom: 10 }}>Key finding</p>
            <h2 style={{ fontSize: "clamp(20px,2.8vw,32px)", fontWeight: 800, lineHeight: 1.1, margin: "0 0 12px" }}>
              The ranking reflects contamination, not capability.
            </h2>
            <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.65, maxWidth: "70ch", margin: "0 0 14px" }}>
              Gemini swept the source bank, so it fails 264 of 267 questions — its 23.6% is nearly arithmetically forced.
              GPT-5.1 helped generate most of the set, leaving only 5 scorable rows.
              {" "}<b>The one uncontaminated number is Claude's 67.3% on 165 rows it never touched.</b>
            </p>
            <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.65, maxWidth: "70ch", margin: 0 }}>
              A vendor with no hand in selecting those questions still fails a third of them. That, and the 16% all three failed, are the numbers worth quoting. The full leaderboard is context.
            </p>
          </div>
        </div>
      </section>

      {/* Main results table */}
      <section className="s-band alt" id="results">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Results</p>
            <h2>Three frontier flagships · same 267 questions</h2>
            <p className="lead">Every model ran the identical set under identical conditions: same prompts, figures attached, high reasoning effort. Sorted by overall accuracy, but read the clean subset column first.</p>
          </div>

          <div style={{ overflowX: "auto", marginTop: 24 }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", fontSize: 13,
              fontFamily: "var(--mono)", minWidth: 580,
            }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--line)" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--muted)", fontWeight: 600, whiteSpace: "nowrap" }}>Model</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Accuracy</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#f5a623", fontWeight: 700 }}>Clean subset</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Scorable rows</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>N</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.model} style={{
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                  }}>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 0 }}>
                        <OrgDot org={r.org} />
                        <span style={{ color: "var(--text)" }}>{r.model}</span>
                      </span>
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--text)", fontWeight: 600 }}>{r.accuracy}%</td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: r.clean_accuracy !== null ? "#f5a623" : "var(--muted)", fontWeight: r.clean_accuracy !== null ? 700 : 400 }}>
                      {r.clean_accuracy !== null
                        ? `${r.clean_accuracy}% (n=${r.n_clean})`
                        : <span style={{ opacity: 0.4 }}>n={r.n_clean} — too few</span>}
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>{r.n_clean} of {r.n_items}</td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>{r.n_items}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "var(--muted-2)", marginTop: 12 }}>
            <b>Clean subset</b> = rows where the model had no role in failing or verifying the question (<code>do_not_score</code> field excluded).
            GPT-5.1 and Gemini 3.1 Pro built almost the entire set, leaving fewer than 10 scorable rows each — those figures are not reported.
          </p>
        </div>
      </section>

      {/* Accuracy bar chart */}
      <section className="s-band">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Accuracy breakdown</p>
            <h2>Overall accuracy on 267 questions.</h2>
            <p className="lead">Models score 96–98% on ordinary JEE questions from the same source. These are only the ones they fail.</p>
          </div>
          <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 10 }}>
            {results.map(r => (
              <div key={r.model} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 210, flexShrink: 0, display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--mono)", fontSize: 12, color: "var(--muted)" }}>
                  <OrgDot org={r.org} />
                  {r.model}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: 4, height: 18, overflow: "hidden" }}>
                  <div style={{
                    width: `${((r.accuracy / maxAccuracy) * 100).toFixed(1)}%`,
                    height: "100%", background: "rgba(245,166,35,0.65)",
                    borderRadius: 4, transition: "width 0.4s",
                  }} />
                </div>
                <div style={{ width: 56, textAlign: "right", fontFamily: "var(--mono)", fontSize: 13, color: "#f5a623", fontWeight: 700 }}>{r.accuracy}%</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Findings summary */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Findings</p>
            <h2>What the benchmark reveals.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 16, marginTop: 24 }}>
            {[
              { n: "F1", title: "Failure-only benchmarks invert contamination", body: "The models that helped build this set have the fewest scorable rows. The ranking is a contamination map, not a capability ranking." },
              { n: "F2", title: "43 questions failed by all three simultaneously", body: "16% of the set. These are the hardest items — all three frontier models, same prompts, figures attached, all wrong." },
              { n: "F3", title: "Claude's clean subset is the honest number", body: "67.3% on 165 rows Claude never touched. A vendor with no hand in selection still fails a third of them." },
              { n: "F4", title: "Answer-key quality is the dominant problem", body: "Across 704 checked questions, 514 turned out to be wrong keys rather than model failures. Every published item has an independently confirmed key." },
              { n: "F5", title: "Two-vendor agreement flags bad keys", body: "When two vendors give the same wrong answer, 81% are broken keys — measured on three independent pools. Those are excluded." },
              { n: "F6", title: "do_not_score is not optional", body: "Scoring Claude across all 267 rows gives 62.2%. On its 165 clean rows it scores 67.3% — higher, because the contaminated rows are specifically questions it got wrong when building the set. The field isn't optional." },
            ].map(f => (
              <div key={f.n} style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px 22px" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#f5a623", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>{f.n}</div>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.6 }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Caveats */}
      <section className="s-band">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Caveats</p>
            <h2>What this benchmark can and cannot claim.</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20, maxWidth: "72ch" }}>
            {[
              { id: "L1", text: "Not usable for GPT-5.1 or Gemini 3.1 Pro Preview — they generated almost the entire failure set, leaving fewer than 10 scorable rows each." },
              { id: "L2", text: "Wrong keys are the dominant failure mode of the source material and are not all gone. Excluded ones were caught by cross-vendor checking, but residual bad keys should be assumed." },
              { id: "L3", text: "Evidence is model-on-model. Model agreement cannot rule out a shared blind spot — a case exists where three models from one vendor agreed and a fourth reached a different, correct answer." },
              { id: "L4", text: "Single-solver rows are weaker than they look. Adding a second vendor changed about one in three verdicts settled by one vendor." },
              { id: "L5", text: "Subject mix is uneven. Models fail Mathematics least, so it is the smallest slice (63 of 267)." },
              { id: "L6", text: "Solutions are not included. Answer keys are; worked explanations are not." },
            ].map(c => (
              <div key={c.id} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#f5a623", fontWeight: 700, flexShrink: 0, paddingTop: 2 }}>{c.id}</span>
                <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", lineHeight: 1.65 }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Methodology</p>
            <h2>How a question qualified.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16, marginTop: 24 }}>
            {[
              { step: "01", title: "Failure gate", body: "A question was included only if a frontier model answered it wrong on a scored measurement — truncations and API errors are lost measurements, not failures." },
              { step: "02", title: "Key confirmation", body: "An independent model re-solved every question without sight of the answer key or the failed model's answer, with figures attached, and reached the same key." },
              { step: "03", title: "Contamination tracking", body: "Every row carries a do_not_score list covering both roles: a model that confirmed a key and a model that failed it. Honest scoring means excluding those rows per model." },
              { step: "04", title: "Held-out split", body: "77 further questions exist and are deliberately unpublished. A benchmark meant to test future models loses value once it is all public. Contact maintainers for held-out evaluation." },
            ].map(s => (
              <div key={s.step} style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px 22px" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted-2)", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 6 }}>{s.step}</div>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.6 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Links */}
      <section className="s-band s-cs-cta">
        <div className="s-wrap" style={{ textAlign: "center" }}>
          <p className="s-eyebrow" style={{ justifyContent: "center" }}>Reproducibility</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: "0 0 8px" }}>Dataset is gated. Held-out split available on request.</h2>
          <p style={{ color: "var(--muted)", maxWidth: "52ch", margin: "0 auto 28px", fontSize: 15 }}>
            267 questions, figures, answer keys, and contamination metadata are available on HuggingFace (HF login required). 77 held-out questions are available for evaluation on request — contact us to benchmark a model fairly.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/NULLSPACE" target="_blank" rel="noopener noreferrer">Full dataset (gated) →</a>
            <Link className="s-btn ghost" to="/blog/nullspace-jee-benchmark-contamination-tracking">Read the blog post →</Link>
            <Link className="s-btn ghost" to="/contact">Submit a model →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
